import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DailyIframe from "@daily-co/daily-js";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, PhoneOff, Video } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdhocMeeting — Req #8 + user request:
 * Hosts the Daily.co room inside the TMC app so when the call ends,
 * we navigate back to the user's dashboard automatically (no orphaned tabs).
 */
export default function AdhocMeeting() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const [meta, setMeta] = useState(null); // {room_url, room_name, topic, is_owner}
  const [status, setStatus] = useState("Loading meeting…");
  const [ended, setEnded] = useState(false);

  const homeRoute =
    user?.roles?.includes("therapist") || user?.roles?.includes("admin_therapist")
      ? "/therapist"
      : user?.roles?.includes("facilitator")
      ? "/facilitator"
      : user?.roles?.includes("admin")
      ? "/admin"
      : "/";

  const goHome = useCallback(() => {
    // Tear down the frame, hit the end endpoint best-effort, then route back.
    try { frameRef.current?.destroy(); } catch (e) { /* ignore */ }
    frameRef.current = null;
    axios
      .post(`${API}/video/adhoc-meeting/${meetingId}/end`)
      .catch(() => { /* best effort */ });
    navigate(homeRoute, { replace: true });
  }, [meetingId, navigate, homeRoute]);

  // Fetch the meeting metadata
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.post(`${API}/video/adhoc-meeting/${meetingId}/token`);
        if (cancelled) return;
        setMeta(r.data);
      } catch (e) {
        if (cancelled) return;
        const msg = e.response?.data?.detail || "Could not load meeting";
        toast.error(msg);
        navigate(homeRoute, { replace: true });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [meetingId, navigate, homeRoute]);

  // Mount the Daily.co iframe once meta is ready
  useEffect(() => {
    if (!meta || !containerRef.current || frameRef.current) return;

    const frame = DailyIframe.createFrame(containerRef.current, {
      showLeaveButton: true,
      showFullscreenButton: true,
      iframeStyle: {
        width: "100%",
        height: "100%",
        border: "0",
        borderRadius: "0.5rem",
      },
    });
    frameRef.current = frame;

    frame.on("loading", () => setStatus("Loading video room…"));
    frame.on("loaded", () => setStatus("Joining…"));
    frame.on("joined-meeting", () => {
      setStatus("Connected");
      toast.success("Connected");
    });

    // CRITICAL: when the user hangs up, route back to their dashboard.
    frame.on("left-meeting", () => {
      setEnded(true);
      setStatus("Call ended");
      toast.success("Returning to TMC TeleTech…");
      // Small delay so the user sees the toast before redirect.
      setTimeout(goHome, 600);
    });

    frame.on("error", (ev) => {
      console.error("Daily.co error:", ev);
      toast.error(ev?.errorMsg || "Video error — returning to dashboard");
      setTimeout(goHome, 1200);
    });

    const joinOpts = { url: meta.room_url, userName: user?.name || "Guest" };
    if (meta.token) joinOpts.token = meta.token;
    frame.join(joinOpts).catch((e) => {
      console.error("join failed:", e);
      toast.error("Could not join the meeting");
      setTimeout(goHome, 1500);
    });

    return () => {
      try { frame.destroy(); } catch (e) { /* ignore */ }
      frameRef.current = null;
    };
  }, [meta, user?.name, goHome]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4">
      <div className="max-w-6xl mx-auto">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goHome}
                  data-testid="adhoc-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to dashboard
                </Button>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Video className="w-5 h-5 text-emerald-600" />
                  Ad-Hoc Meeting
                </CardTitle>
              </div>
              <div className="flex items-center gap-2">
                {meta?.topic && (
                  <span className="text-sm text-muted-foreground truncate max-w-[300px]">
                    {meta.topic}
                  </span>
                )}
                <Badge variant="outline" data-testid="adhoc-status">{status}</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!meta ? (
              <div className="flex items-center justify-center h-[60vh]">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div
                ref={containerRef}
                className="relative bg-slate-900 rounded-lg overflow-hidden w-full"
                style={{ height: "75vh" }}
                data-testid="adhoc-video-container"
              />
            )}
            <div className="mt-3 flex justify-end">
              <Button
                variant="destructive"
                onClick={goHome}
                disabled={ended}
                data-testid="adhoc-leave-btn"
                className="gap-2"
              >
                <PhoneOff className="w-4 h-4" />
                Leave & Return
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
